import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, X } from "lucide-react";
import { MENU } from "@/data/menu";
import { API } from "@/lib/constants";
import { useI18n } from "@/lib/i18n";

// Curated subset that makes sense for takeout
const ORDER_CATEGORIES = ["meals", "wings", "salads", "sandwiches", "pastas", "sides", "drinks", "dessert"];

const TIMES = [
  "11:30", "12:00", "12:30", "13:00", "13:30", "14:00",
  "17:00", "17:30", "18:00", "18:30", "19:00", "19:30",
  "20:00", "20:30", "21:00", "21:30",
];

export default function Order() {
  const { t } = useI18n();
  const today = new Date().toISOString().split("T")[0];
  const [activeCat, setActiveCat] = useState(ORDER_CATEGORIES[0]);
  const [cart, setCart] = useState({}); // { itemName: { qty, category } }
  const [details, setDetails] = useState({
    name: "",
    email: "",
    phone: "",
    pickup_date: today,
    pickup_time: "19:00",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [confirmedId, setConfirmedId] = useState(null);

  const visibleCats = useMemo(
    () => MENU.filter((m) => ORDER_CATEGORIES.includes(m.id)),
    []
  );
  const current = visibleCats.find((c) => c.id === activeCat) ?? visibleCats[0];

  const addItem = (name, category) => {
    setCart((c) => ({
      ...c,
      [name]: { qty: (c[name]?.qty || 0) + 1, category },
    }));
  };
  const decItem = (name) => {
    setCart((c) => {
      const cur = c[name];
      if (!cur) return c;
      if (cur.qty <= 1) {
        const { [name]: _, ...rest } = c;
        return rest;
      }
      return { ...c, [name]: { ...cur, qty: cur.qty - 1 } };
    });
  };
  const removeItem = (name) => {
    setCart((c) => {
      const { [name]: _, ...rest } = c;
      return rest;
    });
  };

  const cartItems = Object.entries(cart).map(([name, v]) => ({ name, ...v }));
  const totalCount = cartItems.reduce((s, i) => s + i.qty, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) {
      toast.error("Add a few dishes first");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        ...details,
        items: cartItems.map((it) => ({
          name: it.name,
          qty: it.qty,
          category: it.category,
        })),
        notes: details.notes || null,
      });
      setConfirmedId(res.data.id);
      toast.success(t("order.success_title"));
    } catch (err) {
      toast.error("Could not place order", {
        description:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Try again",
      });
    } finally {
      setBusy(false);
    }
  };

  if (confirmedId) {
    return (
      <section
        id="order"
        data-testid="order-section"
        className="bg-[#FDFBF7] py-24 md:py-36"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl border border-[#1A1713]/15 p-10 md:p-14 bg-[#F5F2EA]">
            <p className="font-mono-accent text-[12px] text-[#7A2021] mb-4">
              — Order #{confirmedId.slice(0, 8).toUpperCase()}
            </p>
            <h2
              data-testid="order-confirmation"
              className="font-serif-display text-5xl md:text-6xl text-[#1A1713] mb-5"
            >
              {t("order.success_title")}
            </h2>
            <p className="text-[#5C5449] leading-relaxed mb-8">
              We'll have your order ready for pickup on{" "}
              <em>
                {details.pickup_date} at {details.pickup_time}
              </em>
              . A confirmation will arrive at <em>{details.email}</em>.
            </p>
            <button
              onClick={() => {
                setConfirmedId(null);
                setCart({});
              }}
              data-testid="order-new-btn"
              className="font-mono-accent text-[13px] bg-[#1A1713] text-[#FDFBF7] px-7 py-4 hover:bg-[#7A2021] transition-colors"
            >
              Order Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="order"
      data-testid="order-section"
      className="bg-[#FDFBF7] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-12 md:mb-16 border-b border-[#E4DECD] pb-10">
          <p className="font-mono-accent text-[12px] text-[#7A2021] mb-5">
            {t("order.kicker")}
          </p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <h2
              data-testid="order-heading"
              className="font-serif-display text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-[#1A1713]"
            >
              {t("order.heading")}
            </h2>
            <p className="max-w-sm text-[#5C5449] leading-relaxed">
              {t("order.subtitle")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-12">
          {/* Category list + items */}
          <div className="col-span-12 lg:col-span-8">
            <div className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-6 px-6 md:mx-0 md:px-0">
              {visibleCats.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveCat(c.id)}
                  data-testid={`order-cat-${c.id}`}
                  className={`flex-shrink-0 font-mono-accent text-[12px] px-4 py-2.5 transition-colors duration-300 ${
                    activeCat === c.id
                      ? "bg-[#1A1713] text-[#FDFBF7]"
                      : "bg-[#F5F2EA] text-[#1A1713] hover:bg-[#EAE5D9]"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            <ul className="space-y-1">
              {current.items.map((it, idx) => {
                const qty = cart[it.name]?.qty || 0;
                return (
                  <li
                    key={`${current.id}-${idx}`}
                    data-testid="order-item-row"
                    className="flex items-center gap-4 py-4 border-b border-[#E4DECD]"
                  >
                    <span className="font-serif-display text-xl text-[#1A1713] flex-1 leading-snug">
                      {it.name}
                    </span>
                    {qty > 0 ? (
                      <div className="flex items-center gap-3 bg-[#1A1713] text-[#FDFBF7] px-2 py-1.5">
                        <button
                          onClick={() => decItem(it.name)}
                          aria-label="Decrease"
                          data-testid={`order-dec-${current.id}-${idx}`}
                          className="hover:text-[#C98A4B]"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="font-mono-accent text-[13px] min-w-[14px] text-center">
                          {qty}
                        </span>
                        <button
                          onClick={() => addItem(it.name, current.id)}
                          aria-label="Increase"
                          data-testid={`order-inc-${current.id}-${idx}`}
                          className="hover:text-[#C98A4B]"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => addItem(it.name, current.id)}
                        data-testid={`order-add-${current.id}-${idx}`}
                        className="font-mono-accent text-[12px] border border-[#1A1713]/30 text-[#1A1713] px-4 py-2 hover:bg-[#1A1713] hover:text-[#FDFBF7] transition-colors"
                      >
                        {t("order.add")}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Cart */}
          <aside className="col-span-12 lg:col-span-4">
            <div className="bg-[#1A1713] text-[#FDFBF7] p-7 md:p-9 lg:sticky lg:top-32">
              <p className="font-mono-accent text-[12px] text-[#C98A4B] mb-4">
                {t("order.cart_title")} · {totalCount}{" "}
                {totalCount === 1 ? "item" : "items"}
              </p>
              {cartItems.length === 0 ? (
                <p
                  data-testid="cart-empty"
                  className="font-serif-display italic text-2xl text-[#FDFBF7]/70 leading-snug"
                >
                  {t("order.cart_empty")}
                </p>
              ) : (
                <ul data-testid="cart-list" className="space-y-3 mb-6 max-h-72 overflow-y-auto pr-2">
                  {cartItems.map((it) => (
                    <li
                      key={it.name}
                      data-testid="cart-item"
                      className="flex items-center gap-3 border-b border-[#FDFBF7]/10 pb-3"
                    >
                      <span className="font-mono-accent text-[13px] text-[#C98A4B] min-w-[20px]">
                        ×{it.qty}
                      </span>
                      <span className="flex-1 text-sm leading-snug">{it.name}</span>
                      <button
                        onClick={() => removeItem(it.name)}
                        aria-label="Remove"
                        className="text-[#FDFBF7]/60 hover:text-[#7A2021]"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <form onSubmit={submit} data-testid="order-form" className="space-y-4 mt-4">
                <input
                  type="text"
                  required
                  placeholder="Full name"
                  value={details.name}
                  onChange={(e) => setDetails({ ...details, name: e.target.value })}
                  data-testid="order-form-name"
                  className="ldo-cart-input"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="email"
                    required
                    placeholder="Email"
                    value={details.email}
                    onChange={(e) =>
                      setDetails({ ...details, email: e.target.value })
                    }
                    data-testid="order-form-email"
                    className="ldo-cart-input"
                  />
                  <input
                    type="tel"
                    required
                    placeholder="Phone"
                    value={details.phone}
                    onChange={(e) =>
                      setDetails({ ...details, phone: e.target.value })
                    }
                    data-testid="order-form-phone"
                    className="ldo-cart-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    min={today}
                    required
                    value={details.pickup_date}
                    onChange={(e) =>
                      setDetails({ ...details, pickup_date: e.target.value })
                    }
                    data-testid="order-form-date"
                    className="ldo-cart-input"
                  />
                  <select
                    required
                    value={details.pickup_time}
                    onChange={(e) =>
                      setDetails({ ...details, pickup_time: e.target.value })
                    }
                    data-testid="order-form-time"
                    className="ldo-cart-input"
                  >
                    {TIMES.map((t) => (
                      <option key={t} value={t} className="text-[#1A1713]">
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  rows={2}
                  placeholder="Allergies, packaging notes…"
                  value={details.notes}
                  onChange={(e) =>
                    setDetails({ ...details, notes: e.target.value })
                  }
                  data-testid="order-form-notes"
                  className="ldo-cart-input resize-none"
                />

                <button
                  type="submit"
                  disabled={busy || cartItems.length === 0}
                  data-testid="order-form-submit"
                  className="w-full font-mono-accent text-[13px] bg-[#C98A4B] text-[#1A1713] px-6 py-4 hover:bg-[#FDFBF7] transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? "…" : t("order.submit")}
                </button>
              </form>
            </div>
          </aside>
        </div>
      </div>
      <style>{`
        .ldo-cart-input {
          width: 100%; background: transparent; color: #FDFBF7;
          border: 0; border-bottom: 1px solid rgba(253,251,247,0.2);
          padding: 10px 0; font-family: 'DM Sans', sans-serif; font-size: 14px;
          outline: none; transition: border-color .3s ease;
        }
        .ldo-cart-input::placeholder { color: rgba(253,251,247,0.4); }
        .ldo-cart-input:focus { border-bottom-color: #C98A4B; }
        .ldo-cart-input option { background:#1A1713; }
      `}</style>
    </section>
  );
}
